import { InteractiveContainer } from "@jugl-web/ui-components/cross-platform/InteractiveContainer";
import { PlainButton } from "@jugl-web/ui-components/cross-platform/PlainButton";
import { memo, useEffect, useState } from "react";
import {
  getFileImagePath,
  getFileType,
} from "../../../../../tasks/components/TaskAttachments/utils";
import { MAX_FILES_COUNT } from "../../consts";
import { ReactComponent as DeleteIcon } from "./assets/delete.svg";
import { ReactComponent as PlayIcon } from "./assets/play.svg";
import { ReactComponent as PlusIcon } from "./assets/plus.svg";

const FilePreviewImage = memo<{ file: File; isImage?: boolean }>(
  ({ file, isImage }) => {
    const [previewUrl, setPreviewUrl] = useState<string | undefined>(undefined);

    useEffect(() => {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);

      return () => {
        URL.revokeObjectURL(url);
        setPreviewUrl(undefined);
      };
    }, [file]);

    const className = "h-full w-full object-cover";

    return isImage ? (
      <img src={previewUrl} alt={file.name} className={className} />
    ) : (
      <video src={previewUrl} className={className} muted preload="metadata" />
    );
  }
);

interface AttachmentCardProps {
  name: string;
  file: File;
  onRemove?: () => void;
}

const AttachmentCard: React.FC<AttachmentCardProps> = ({
  name,
  file,
  onRemove,
}) => {
  const fileType = getFileType(name);
  const isImage = fileType === "image";
  const isVideo = fileType === "video";

  return (
    <div className="group relative flex h-[120px] w-[120px] shrink-0 flex-col items-center justify-center gap-2 overflow-hidden rounded-lg border border-solid border-grey-200 bg-gradient-to-b from-white to-[#CDE2F2] select-none">
      {file && (isImage || isVideo) ? (
        <>
          <FilePreviewImage file={file} isImage={isImage} />
          {isVideo && (
            <PlayIcon className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
          )}
        </>
      ) : (
        <>
          <img
            src={getFileImagePath(name, "sm")}
            alt={name}
            className="max-h-10 max-w-10"
          />
          <span className="w-full truncate px-2 text-center font-secondary text-xs text-dark-800">
            {name}
          </span>
        </>
      )}
      {onRemove && (
        <PlainButton
          className="pointer-events-auto absolute top-0 right-0 flex h-8 w-8 items-center justify-center rounded-tr-lg rounded-bl-[14px] bg-dark/20"
          onClick={onRemove}
        >
          <DeleteIcon />
        </PlainButton>
      )}
    </div>
  );
};

interface CommentsAttachmentPreviewProps {
  attachments: File[];
  onRemove: (index: number) => void;
  onAdd: () => void;
  readOnly?: boolean;
}

export const CommentsAttachmentPreview: React.FC<
  CommentsAttachmentPreviewProps
> = ({ attachments, onRemove, onAdd, readOnly }) => {
  const hasAny = attachments.length > 0;

  if (!hasAny) return null;

  return (
    <div className="jugl__custom-scrollbar-horizontal flex items-center gap-3 overflow-x-auto px-3 pt-3 pb-1.5">
      {attachments.map((file, index) => (
        <AttachmentCard
          key={`new-${+index}`}
          name={file.name}
          file={file}
          onRemove={!readOnly ? () => onRemove(index) : undefined}
        />
      ))}
      {!readOnly && attachments.length < MAX_FILES_COUNT && (
        <InteractiveContainer
          className="flex h-[120px] w-[120px] shrink-0 items-center justify-center rounded-lg border border-solid border-grey-200 bg-grey-200 transition-colors hover:bg-grey-300"
          onClick={onAdd}
        >
          <PlusIcon />
        </InteractiveContainer>
      )}
    </div>
  );
};
